import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { useApi } from "../../../../../http/api";
import {
  AdminBorderSystem,
  InsertAdminBorderSystemBody,
  UpdateBorderSystemNewImageBody,
  UpdateBorderSystemRemoveImageBody,
} from "../../../../../http/border/types/borderSystems";
import { useBorderSystemsApi } from "../../../../../http/border/useBorderSystems";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import useImageUploader from "../../../../ui/ImageUploader";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import Switch from "../../../../ui/Switch";
import Textarea from "../../../../ui/Textarea";
import TranslatableField from "../../../../ui/TranslatableField";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";

export interface SystemFormBody {
  name: string | null;
  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;
  description: string | null;
  descriptionIt: string | null;
  descriptionDe: string | null;
  descriptionEs: string | null;
  descriptionFr: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  isSubstructureRequired: boolean;
  isVerticalBaseTypeRequired: boolean;
  isBallastRequired: boolean;
  isWaterproofKitVisible: boolean;
  isWedgeVisible: boolean;
  isFasteningVisible: boolean;
  isSubstructureDetailsRequired: boolean;
  isPerimeterSplitRequired: boolean;
  isCornerOrientationRequired: boolean;
  isCurbVisible: boolean;
  isCurbDepthVisible: boolean;
}

interface SystemFormProps {
  isLoading?: boolean;
  isDisabled?: boolean;
  borderSystem?: AdminBorderSystem;
}

const SystemForm: FC<SystemFormProps> = ({ isLoading = false, isDisabled, borderSystem }) => {
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const navigate = useNavigate();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const borderSystemsApi = useBorderSystemsApi();
  const api = useApi();

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: borderSystem?.image,
    isLoading: isLoading && !borderSystem,
    disabled: isDisabled,
  });

  const onSubmit = () => async (formData: SystemFormBody) => {
    setIsSubmitting(true);

    const common: InsertAdminBorderSystemBody = {
      name: formData.name?.trim() ?? "",
      nameIt: formData.nameIt?.trim() ?? null,
      nameDe: formData.nameDe?.trim() ?? null,
      nameEs: formData.nameEs?.trim() ?? null,
      nameFr: formData.nameFr?.trim() ?? null,
      description: formData.description?.trim() ?? "",
      descriptionIt: formData.descriptionIt?.trim() ?? null,
      descriptionDe: formData.descriptionDe?.trim() ?? null,
      descriptionEs: formData.descriptionEs?.trim() ?? null,
      descriptionFr: formData.descriptionFr?.trim() ?? null,
      order: formData.order!,
      visibility: formData.visibility,
      isSubstructureRequired: formData.isSubstructureRequired,
      isVerticalBaseTypeRequired: formData.isVerticalBaseTypeRequired,
      isBallastRequired: formData.isBallastRequired,
      isWaterproofKitVisible: formData.isWaterproofKitVisible,
      isWedgeVisible: formData.isWedgeVisible,
      isFasteningVisible: formData.isFasteningVisible,
      isSubstructureDetailsRequired: formData.isSubstructureDetailsRequired,
      isPerimeterSplitRequired: formData.isPerimeterSplitRequired,
      isCornerOrientationRequired: formData.isCornerOrientationRequired,
      isCurbVisible: formData.isCurbVisible,
      isCurbDepthVisible: formData.isCurbDepthVisible,
    };

    if (borderSystem?.id) {
      const update = async (body: UpdateBorderSystemRemoveImageBody | UpdateBorderSystemNewImageBody) => {
        try {
          await borderSystemsApi.updateAdminBorderSystem(borderSystem?.id!, body);
          dispatch(showSuccessNotification(i18n.translation));
          onSave();
        } catch (err) {
          dispatch(showErrorNotification(i18n, err));
        } finally {
          setIsSubmitting(false);
        }
      };
      if (newImage) {
        try {
          const uploadPolicy = await borderSystemsApi.generateUploadImageUrl(
            borderSystem?.id!,
            newImage.name,
            newImage.type,
          );
          const res = await api.uploadFile(uploadPolicy, newImage);
          if (!res.ok) {
            dispatch(showErrorNotification(i18n, res));
            setIsSubmitting(false);
            return;
          }
          const body: UpdateBorderSystemNewImageBody = {
            ...common,
            image: newImage.name,
          };
          await update(body);
        } catch (error) {
          dispatch(showErrorNotification(i18n, error));
        } finally {
          setIsSubmitting(false);
        }
      } else {
        const body: UpdateBorderSystemRemoveImageBody = {
          ...common,
          removeImage: removeImage,
        };
        await update(body);
      }
    } else {
      const redirect = (id: string) => {
        setIsSubmitting(false);
        navigate(`/admin/systems/${systemId}/border_systems/${id}`);
        onSave();
      };
      const body: InsertAdminBorderSystemBody = {
        ...common,
        image: newImage?.name,
      };

      try {
        const res = await borderSystemsApi.insertAdminBorderSystem(body);
        if (newImage) {
          const uploadPolicy = await borderSystemsApi.generateUploadImageUrl(res.id, newImage.name, newImage.type);
          const uploadResponse = await api.uploadFile(uploadPolicy, newImage);
          if (!uploadResponse.ok) {
            dispatch(showErrorNotification(i18n, res));
            return;
          }
          redirect(res.id);
        } else {
          redirect(res.id);
        }
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Form<SystemFormBody>
      defaultValues={
        isLoading
          ? undefined
          : {
              name: null,
              nameIt: borderSystem?.nameIt ?? null,
              nameDe: borderSystem?.nameDe ?? null,
              nameEs: borderSystem?.nameEs ?? null,
              nameFr: borderSystem?.nameFr ?? null,
              description: borderSystem?.description ?? null,
              descriptionIt: borderSystem?.descriptionIt ?? null,
              descriptionDe: borderSystem?.descriptionDe ?? null,
              descriptionEs: borderSystem?.descriptionEs ?? null,
              descriptionFr: borderSystem?.descriptionFr ?? null,
              order: borderSystem?.order ?? 50,
              visibility: borderSystem?.visibility ?? "hidden",
              isBallastRequired: borderSystem?.isBallastRequired,
              isFasteningVisible: borderSystem?.isFasteningVisible,
              isSubstructureRequired: borderSystem?.isSubstructureRequired,
              isVerticalBaseTypeRequired: borderSystem?.isVerticalBaseTypeRequired,
              isWaterproofKitVisible: borderSystem?.isWaterproofKitVisible,
              isWedgeVisible: borderSystem?.isWedgeVisible,
              isSubstructureDetailsRequired: borderSystem?.isSubstructureDetailsRequired,
              isPerimeterSplitRequired: borderSystem?.isPerimeterSplitRequired,
              isCornerOrientationRequired: borderSystem?.isCornerOrientationRequired,
              isCurbVisible: borderSystem?.isCurbVisible,
              isCurbDepthVisible: borderSystem?.isCurbDepthVisible,
            }
      }
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isLoading || isSubmitting}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, control, setValue, watch, reset }) => {
        useEffect(() => {
          register("nameIt");
          register("nameDe");
          register("nameEs");
          register("nameFr");
          register("descriptionIt");
          register("descriptionDe");
          register("descriptionEs");
          register("descriptionFr");
        }, [register]);

        const values = watch();
        const isSubstructureRequired = !!watch("isSubstructureRequired");
        const isVerticalBaseTypeRequired = !!watch("isVerticalBaseTypeRequired");
        const isBallastRequired = !!watch("isBallastRequired");
        const isWaterproofKitVisible = !!watch("isWaterproofKitVisible");
        const isWedgeVisible = !!watch("isWedgeVisible");
        const isFasteningVisible = !!watch("isFasteningVisible");
        const isSubstructureDetailsRequired = !!watch("isSubstructureDetailsRequired");
        const isPerimeterSplitRequired = !!watch("isPerimeterSplitRequired");
        const isCornerOrientationRequired = !!watch("isCornerOrientationRequired");
        const isCurbVisible = !!watch("isCurbVisible");
        const isCurbDepthVisible = !!watch("isCurbDepthVisible");

        useEffect(() => {
          borderSystem && reset(borderSystem);
        }, [borderSystem]);

        return (
          <FormFieldsContainer imageUploader={component}>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isInvalid, isOptional }) => (
                <TranslatableField
                  field="name"
                  formValues={values}
                  setFormValue={setValue}
                  dialogTitle={i18n.translation.common.name}
                >
                  <Input
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={isDisabled || isLoading || isSubmitting}
                    {...register("name", {
                      required: !isOptional,
                      minLength: 1,
                    })}
                  />
                </TranslatableField>
              )}
            </FormField>

            <FormField label={i18n.translation.common.description} error={errors.description}>
              {({ labelId, isOptional, isInvalid }) => (
                <TranslatableField
                  field="description"
                  formValues={values}
                  setFormValue={setValue}
                  dialogTitle={i18n.translation.common.description}
                  dialogType="textarea"
                >
                  <Textarea
                    id={labelId}
                    isInvalid={isInvalid}
                    disabled={isDisabled || isLoading || isSubmitting}
                    {...register("description", {
                      required: !isOptional,
                      minLength: 1,
                    })}
                  />
                </TranslatableField>
              )}
            </FormField>

            <FormField label={i18n.translation.common.isSubstructureRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  disabled={isDisabled || isLoading || isSubmitting}
                  id={labelId}
                  checked={isSubstructureRequired}
                  onChange={(checked) => setValue("isSubstructureRequired", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.isVerticalBaseTypeRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  disabled={isDisabled || isLoading || isSubmitting}
                  id={labelId}
                  checked={isVerticalBaseTypeRequired}
                  onChange={(checked) => setValue("isVerticalBaseTypeRequired", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.isBallastRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  disabled={isDisabled || isLoading || isSubmitting}
                  id={labelId}
                  checked={isBallastRequired}
                  onChange={(checked) => setValue("isBallastRequired", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.isWaterproofKitVisible} isOptional>
              {({ labelId }) => (
                <Switch
                  disabled={isDisabled || isLoading || isSubmitting}
                  id={labelId}
                  checked={isWaterproofKitVisible}
                  onChange={(checked) => setValue("isWaterproofKitVisible", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.isWedgeVisible} isOptional>
              {({ labelId }) => (
                <Switch
                  disabled={isDisabled || isLoading || isSubmitting}
                  id={labelId}
                  checked={isWedgeVisible}
                  onChange={(checked) => setValue("isWedgeVisible", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.isFasteningVisible} isOptional>
              {({ labelId }) => (
                <Switch
                  disabled={isDisabled || isLoading || isSubmitting}
                  id={labelId}
                  checked={isFasteningVisible}
                  onChange={(checked) => setValue("isFasteningVisible", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.isSubstructureDetailsRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  disabled={isDisabled || isLoading || isSubmitting}
                  id={labelId}
                  checked={isSubstructureDetailsRequired}
                  onChange={(checked) => setValue("isSubstructureDetailsRequired", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.isPerimeterSplitRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  disabled={isDisabled || isLoading || isSubmitting}
                  id={labelId}
                  checked={isPerimeterSplitRequired}
                  onChange={(checked) => setValue("isPerimeterSplitRequired", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.isCornerOrientationRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  disabled={isDisabled || isLoading || isSubmitting}
                  id={labelId}
                  checked={isCornerOrientationRequired}
                  onChange={(checked) => setValue("isCornerOrientationRequired", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.isCurbVisible} isOptional>
              {({ labelId }) => (
                <Switch
                  disabled={isDisabled || isLoading || isSubmitting}
                  id={labelId}
                  checked={isCurbVisible}
                  onChange={(checked) => setValue("isCurbVisible", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.isCurbDepthVisible} isOptional>
              {({ labelId }) => (
                <Switch
                  disabled={isDisabled || isLoading || isSubmitting}
                  id={labelId}
                  checked={isCurbDepthVisible}
                  onChange={(checked) => setValue("isCurbDepthVisible", checked)}
                />
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope
                      {...field}
                      inputId={labelId}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      onChange={(value) => setValue("visibility", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      decimalScale={0}
                      allowNegative={false}
                      disabled={isDisabled || isLoading || isSubmitting}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default SystemForm;
